import type { ApiResponse } from '@atlas/shared';
import { reportDataWrite } from './data-sync';

// Injected by webpack DefinePlugin at build time. Empty string in a normal
// web build → same-origin `/api/v1` (webpack dev proxy / prod same-origin).
// For the Capacitor native build the app is served from capacitor://localhost
// with no same-origin backend, so the target backend origin is baked in via
// `API_BASE_URL=…` → `<origin>/api/v1`. CapacitorHttp handles the cross-origin
// transport + cookies natively, so no server CORS allowlist is required.
declare const __API_BASE_URL__: string;
const BASE_URL = `${(__API_BASE_URL__ || '').replace(/\/+$/, '')}/api/v1`;

// Injected by webpack DefinePlugin. True only in the native-app-shell embedded
// build, where the app runs inside a cross-origin iframe (served from the
// shell's WebView origin, talking to an absolute API origin). In that context
// the httpOnly, SameSite=strict refresh cookie is NEVER sent to the API, so
// auth switches to a "bearer transport": the refresh token travels in the
// request/response BODY and is persisted client-side. On web this is false and
// the cookie flow below is unchanged.
declare const __EMBEDDED__: boolean;
export const EMBEDDED = typeof __EMBEDDED__ !== 'undefined' && __EMBEDDED__;

const STORAGE_KEY = 'atlas.accessToken';

// Embedded builds persist the rotating refresh token in localStorage so the
// session survives a cold boot (sessionStorage does not). Blast radius is
// bounded by the server's short refresh TTL + rotation-on-every-use. The web
// build never stores it — the httpOnly cookie remains the source of truth.
const REFRESH_STORAGE_KEY = 'atlas.refreshToken';

export function getStoredRefreshToken(): string | null {
  if (!EMBEDDED) return null;
  try { return localStorage.getItem(REFRESH_STORAGE_KEY); } catch { return null; }
}
function setStoredRefreshToken(token: string | null) {
  if (!EMBEDDED) return;
  try {
    if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token);
    else localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch { /* localStorage may be unavailable */ }
}
export function clearStoredRefreshToken() { setStoredRefreshToken(null); }

// Pull a rotated refresh token out of any auth response body (login, magic-link
// verify, refresh) when running embedded. No-op on web.
function captureRefreshToken(data: unknown) {
  if (!EMBEDDED || !data || typeof data !== 'object') return;
  const t = (data as { refreshToken?: unknown }).refreshToken;
  if (typeof t === 'string' && t) setStoredRefreshToken(t);
}

// Header that tells the server to use the body transport for this client.
function bodyTransportHeaders(): Record<string, string> {
  return EMBEDDED ? { 'x-refresh-transport': 'body' } : {};
}
/** Log the user out after this much continuous inactivity. Active users get
 *  silent token refreshes ahead of expiry forever; idle users hit logout when
 *  this threshold trips. Tune via `setIdleTimeoutMs` if the call-site wants.
 *
 *  Default: 0 = idle logout DISABLED. The auth store applies the
 *  admin-configurable `session.idleTimeoutMinutes` app-setting after each
 *  /auth/me, so enabling it is a one-row change in Settings → App Settings
 *  (set the value to a positive number of minutes). */
let idleTimeoutMs = 0; // disabled by default
const ACTIVITY_THROTTLE_MS = 5_000;

let lastActivityAt = Date.now();
let lastActivityWriteAt = 0;
let idleCheckInterval: ReturnType<typeof setInterval> | null = null;

/** Update the activity heartbeat. Throttled so a user moving the mouse
 *  doesn't burn CPU on every tick. Called from user-input listeners and
 *  from each authenticated API request. */
function recordActivity() {
  const now = Date.now();
  if (now - lastActivityWriteAt >= ACTIVITY_THROTTLE_MS) {
    lastActivityAt = now;
    lastActivityWriteAt = now;
  }
}

/** Override the default 30-minute idle ceiling at runtime (e.g. set to 1h
 *  per the user's environment). Pass 0 to disable idle logout entirely. */
export function setIdleTimeoutMs(ms: number) { idleTimeoutMs = ms; }

function isIdle(): boolean {
  return idleTimeoutMs > 0 && Date.now() - lastActivityAt > idleTimeoutMs;
}

/** Bind activity listeners + start the periodic idle checker. Idempotent —
 *  safe to call from module load and HMR. */
function startIdleEnforcement() {
  if (typeof window === 'undefined' || idleCheckInterval) return;
  const events: Array<keyof WindowEventMap> = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
  for (const evt of events) {
    window.addEventListener(evt, recordActivity as EventListener, { passive: true });
  }
  // Tick every 30s so a user idle for `idleTimeoutMs` is logged out within
  // half a minute of crossing the threshold.
  idleCheckInterval = setInterval(() => {
    if (!getAccessToken()) return;
    if (isIdle()) fireAuthFailure();
  }, 30_000);
}

// Persist the access token in sessionStorage so HMR / dev rebundles don't lose it.
// Without this, every saved file change forces a /auth/refresh, and concurrent
// refreshes race on the rotating refresh-token cookie and log the user out.
let accessToken: string | null = (() => {
  try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
})();
let refreshPromise: Promise<string | null> | null = null;
let scheduledRefresh: ReturnType<typeof setTimeout> | null = null;

/** Decode a JWT's `exp` (seconds since epoch) without verifying — purely so the
 *  client can schedule a proactive refresh ahead of expiry. Returns null if the
 *  token isn't a JWT or has no exp. */
function decodeExp(token: string): number | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json?.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

/** Schedule a refresh ~60s before the token expires. Cancels any previous
 *  schedule. Active users get silent refreshes forever; idle users skip the
 *  refresh and the periodic idle checker fires logout after the threshold. */
function scheduleProactiveRefresh(token: string) {
  if (scheduledRefresh) { clearTimeout(scheduledRefresh); scheduledRefresh = null; }
  const exp = decodeExp(token);
  if (!exp) return;
  const expiresAtMs = exp * 1000;
  const refreshAtMs = expiresAtMs - 60_000;
  const delay = refreshAtMs - Date.now();
  const tick = () => {
    if (isIdle()) return; // let the idle checker handle logout
    void refreshAccessToken();
  };
  if (delay <= 0) { tick(); return; }
  scheduledRefresh = setTimeout(tick, delay);
}

export function setAccessToken(token: string | null) {
  accessToken = token;
  try {
    if (token) sessionStorage.setItem(STORAGE_KEY, token);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* sessionStorage may be unavailable in some contexts */ }
  if (token) scheduleProactiveRefresh(token);
  else if (scheduledRefresh) { clearTimeout(scheduledRefresh); scheduledRefresh = null; }
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Hooked by the auth store at startup so a refresh failure can trigger a
 *  full client logout (clear state, redirect to login) instead of leaving
 *  the UI in a half-authenticated empty state. */
let onAuthFailureCallback: (() => void) | null = null;
export function onAuthFailure(cb: () => void) { onAuthFailureCallback = cb; }
function fireAuthFailure() {
  setAccessToken(null);
  // Session is dead — drop the persisted refresh token too (embedded only).
  clearStoredRefreshToken();
  try { onAuthFailureCallback?.(); } catch { /* ignore */ }
}

// ── Cross-tab auth sync ─────────────────────────────────────────────────
// Access tokens live in sessionStorage (per-tab on purpose so multiple
// tabs can hold different states briefly), so a second tab doesn't know
// when the first logs in or out. A BroadcastChannel fixes that — tabs of
// the same origin receive events from each other. Private-browsing
// windows are intentionally NOT on the channel (they run in a separate
// browsing context), so signing out of a normal window does NOT terminate
// a parallel private-window session.
const authChannel: BroadcastChannel | null =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('atlas-auth')
    : null;

/** Tell every other open tab that the current user just signed in or out.
 *  The receiving tab decides what to do (re-refresh its access token from
 *  the now-shared cookie, or clear its state and bounce to login). */
export function broadcastAuthChange(type: 'sign-in' | 'sign-out') {
  authChannel?.postMessage({ type, ts: Date.now() });
}

let crossTabSignInHandler: (() => void) | null = null;
/** Auth store hook — called when another tab broadcasts a sign-in. The
 *  receiver re-fetches /auth/me via the refresh cookie so its own state
 *  matches what the user just did in the other tab. */
export function onCrossTabSignIn(cb: () => void) { crossTabSignInHandler = cb; }

if (authChannel) {
  authChannel.addEventListener('message', (e: MessageEvent) => {
    const msg = e.data;
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'sign-out') {
      // Another tab logged out. Clear our local token + let the auth store
      // wipe its state and redirect to /auth/login (same path as a refresh
      // failure). Don't re-broadcast or we'd ping-pong forever.
      fireAuthFailure();
    } else if (msg.type === 'sign-in') {
      // Another tab logged in. If this tab is already authenticated we
      // ignore (its session is still valid); otherwise the auth store
      // hook re-fetches /auth/me using the now-shared refresh cookie.
      if (!accessToken) crossTabSignInHandler?.();
    } else if (msg.type === 'token-refreshed') {
      // Sibling tab just rotated the refresh cookie and got a fresh access
      // token. Adopt it so our next API call doesn't try to refresh with
      // the now-stale cookie — that was the "logged out at 2 minutes" race
      // where two tabs' proactive-refresh timers fired against the same
      // single-use cookie and one came back with a 401.
      if (typeof msg.token === 'string' && msg.token) setAccessToken(msg.token);
    }
  });
}

/** Actually hit the server's /auth/refresh. Pulled out of `refreshAccessToken`
 *  so the network step can be wrapped in a cross-tab lock without duplicating
 *  the response-handling logic. */
async function doNetworkRefresh(): Promise<string | null> {
  try {
    const storedRefresh = getStoredRefreshToken();
    // Embedded: the only credential is the stored refresh token. If it's gone
    // (cleared on logout / never captured), there's nothing to refresh with —
    // fail fast to the login flow instead of firing a doomed request.
    if (EMBEDDED && !storedRefresh) return null;
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(EMBEDDED ? { 'Content-Type': 'application/json' } : {}),
        ...bodyTransportHeaders(),
      },
      ...(EMBEDDED ? { body: JSON.stringify({ refreshToken: storedRefresh }) } : {}),
    });
    if (!res.ok) return null;
    const data: ApiResponse<{ accessToken: string; refreshToken?: string }> = await res.json();
    if (data.success && data.data?.accessToken) {
      const tok = data.data.accessToken;
      setAccessToken(tok);
      // Persist the rotated refresh token (embedded only) — the presented one
      // is now single-use-consumed on the server.
      captureRefreshToken(data.data);
      // Tell sibling tabs they don't need to refresh themselves — the
      // refresh cookie has rotated and any concurrent /auth/refresh from
      // them would get a 401 from the server's single-use guard.
      authChannel?.postMessage({ type: 'token-refreshed', token: tok, ts: Date.now() });
      return tok;
    }
    return null;
  } catch {
    return null;
  }
}

// All refresh paths funnel through this dedup'd helper.
//
//   • Same-tab dedup: `refreshPromise` shares one in-flight promise with
//     every concurrent caller.
//   • Cross-tab dedup: `navigator.locks.request('atlas-refresh', …)`
//     serialises /auth/refresh calls across every tab of this origin so
//     the server's rotating single-use refresh-cookie isn't consumed by
//     one tab and then re-used by another (which would 401 and bounce
//     the loser to /auth/login).
//
// Inside the lock we re-check whether our `accessToken` changed while we
// waited — a sibling tab's `token-refreshed` broadcast may have already
// landed us a fresh token, in which case we skip the network call.
function refreshAccessToken(): Promise<string | null> {
  // Idle gate: if the user has been inactive past the threshold, refuse to
  // refresh and trigger a hard logout. The cookie itself is still valid (30d
  // by default), but we don't want a stale browser tab quietly extending the
  // session forever.
  if (isIdle()) {
    fireAuthFailure();
    return Promise.resolve(null);
  }
  if (refreshPromise) return refreshPromise;

  // Snapshot the token AT call-time so we can tell, inside the lock,
  // whether a sibling-tab broadcast has updated it in the meantime.
  const tokenAtCallTime = accessToken;
  const locks: any = (typeof navigator !== 'undefined' ? (navigator as any).locks : null);
  const supportsLocks = !!locks?.request;

  refreshPromise = (async () => {
    try {
      const work = async (): Promise<string | null> => {
        // Adopt the sibling's token if one arrived while we were queued.
        if (accessToken && accessToken !== tokenAtCallTime) return accessToken;
        return doNetworkRefresh();
      };
      if (supportsLocks) return await locks.request('atlas-refresh', work);
      return await work();
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function ensureToken(): Promise<string | null> {
  if (accessToken) return accessToken;
  return refreshAccessToken();
}

// Reschedule on module load so a token persisted across HMR/refresh keeps
// its proactive refresh timer; also start the idle enforcement listeners.
if (accessToken) scheduleProactiveRefresh(accessToken);
startIdleEnforcement();

export async function api<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  // A user-initiated API call counts as activity; React Query's polling
  // does NOT (it doesn't go through this code path independently of mounts,
  // so we treat all calls the same — fine for an admin app).
  recordActivity();
  const token = await ensureToken();

  // Only declare a JSON content-type when there's an actual body — Fastify rejects
  // body-less POSTs with `Content-Type: application/json` ("body cannot be empty").
  const headers: Record<string, string> = {
    ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...bodyTransportHeaders(),
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  // If 401, try refreshing token once. If refresh itself fails we treat the
  // session as dead — fire the registered logout callback so the user lands
  // on /auth/login instead of staring at an empty list.
  //
  // `&& token` is deliberate, and is NOT the bug it looks like. A request made
  // with no token has ALREADY been through `ensureToken()` above, which calls
  // `refreshAccessToken()` for exactly that case — so a cold tab (sessionStorage
  // is per-tab and is not copied into a new tab) recovers from the httpOnly
  // cookie before the request is even sent. Verified 2026-09-04 in a fresh tab:
  // `POST /auth/refresh` 200, page loads, no login prompt.
  //
  // Dropping this guard therefore fixes nothing. It just fires a SECOND refresh
  // after the first has already failed, adding an unauthenticated request per
  // page load to an endpoint with no rate limit. Retry only when we HAD a token,
  // i.e. when it may simply have expired mid-session.
  if (res.status === 401 && token) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
        credentials: 'include',
      });
    } else {
      fireAuthFailure();
    }
  }

  const data: ApiResponse<T> = await res.json();

  // Login / magic-link verify return a rotated refresh token in the body when
  // this client uses the body transport — capture it (embedded only, no-op web).
  captureRefreshToken((data as { data?: unknown })?.data);

  if (!res.ok && !data.error) {
    throw new Error(`API error: ${res.status}`);
  }

  // A successful write means some query family is now stale — report it so
  // data-sync refreshes the affected caches in this tab and every other tab.
  const method = (options.method ?? 'GET').toString().toUpperCase();
  if (method !== 'GET' && res.ok && data.success !== false) {
    reportDataWrite(path);
  }

  return data;
}

/**
 * Authenticated fetch that returns the raw `Response` (not parsed JSON) —
 * for binary downloads like CSV / PDF exports. Adds the bearer token, retries
 * once on 401, and leaves the caller to read `res.blob()`. Throws on non-OK so
 * callers can surface a toast.
 */
export async function apiBlob(path: string, options: RequestInit = {}): Promise<Response> {
  recordActivity();
  const token = await ensureToken();

  const headers: Record<string, string> = {
    ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res = await fetch(`${BASE_URL}${path}`, { ...options, headers, credentials: 'include' });

  // See the note in `api()` — `ensureToken()` has already refreshed when there
  // was no token, so retry only for a token that expired mid-request.
  if (res.status === 401 && token) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(`${BASE_URL}${path}`, { ...options, headers, credentials: 'include' });
    } else {
      fireAuthFailure();
    }
  }

  if (!res.ok) {
    // The error body is JSON ({ error: { message } }); surface its message.
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      message = data?.error?.message || message;
    } catch { /* non-JSON body */ }
    throw new Error(message);
  }
  return res;
}

export async function apiUpload<T = unknown>(
  path: string,
  formData: FormData
): Promise<ApiResponse<T>> {
  const token = await ensureToken();

  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  const data = await res.json();
  if (res.ok && data?.success !== false) reportDataWrite(path);
  return data;
}
